import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import {
    EmailIcon,
    EmailShareButton,
    FacebookIcon,
    FacebookShareButton,
    RedditIcon,
    RedditShareButton,
    TelegramIcon,
    TelegramShareButton,
    TwitterShareButton,
    WhatsappIcon,
    WhatsappShareButton,
} from 'react-share';

const ICON_SIZE = 56;
const COPIED_RESET_MS = 2000;

// react-share 4.x chi co icon chim Twitter cu, tu ve logo X hien tai.
const XIcon = ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="32" fill="#000" />
        <path
            transform="translate(20 20)"
            fill="#fff"
            d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
        />
    </svg>
);

// Clipboard API chi chay tren HTTPS/localhost; con lai chon san noi dung o input
// roi dung execCommand('copy') (van con duoc cac trinh duyet ho tro).
const copyText = async (text, inputElement) => {
    try {
        if (window.isSecureContext && navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (error) {
        // Bi tu choi quyen clipboard -> thu cach du phong ben duoi.
    }

    if (!inputElement) {
        return false;
    }

    inputElement.focus();
    inputElement.select();

    try {
        return document.execCommand('copy');
    } catch (error) {
        return false;
    }
};

export default function ShareVideoModal({ show, onHide, url, title }) {
    const inputRef = useRef(null);
    // 'idle' | 'copied' | 'failed'
    const [copyStatus, setCopyStatus] = useState('idle');

    useEffect(() => {
        if (copyStatus !== 'copied') {
            return undefined;
        }

        const timer = setTimeout(() => setCopyStatus('idle'), COPIED_RESET_MS);
        return () => clearTimeout(timer);
    }, [copyStatus]);

    useEffect(() => {
        if (!show) {
            setCopyStatus('idle');
        }
    }, [show]);

    const handleCopy = async () => {
        const copied = await copyText(url, inputRef.current);
        setCopyStatus(copied ? 'copied' : 'failed');
    };

    const targets = [
        {
            label: 'Facebook',
            render: (content) => (
                <FacebookShareButton url={url} quote={title} hashtag="#LinkLearnVN">{content}</FacebookShareButton>
            ),
            icon: <FacebookIcon size={ICON_SIZE} round />,
        },
        {
            label: 'WhatsApp',
            render: (content) => (
                <WhatsappShareButton url={url} title={title} separator=" - ">{content}</WhatsappShareButton>
            ),
            icon: <WhatsappIcon size={ICON_SIZE} round />,
        },
        {
            label: 'Telegram',
            render: (content) => <TelegramShareButton url={url} title={title}>{content}</TelegramShareButton>,
            icon: <TelegramIcon size={ICON_SIZE} round />,
        },
        {
            label: 'X',
            render: (content) => <TwitterShareButton url={url} title={title}>{content}</TwitterShareButton>,
            icon: <XIcon size={ICON_SIZE} />,
        },
        {
            label: 'Reddit',
            render: (content) => <RedditShareButton url={url} title={title}>{content}</RedditShareButton>,
            icon: <RedditIcon size={ICON_SIZE} round />,
        },
        {
            label: 'Email',
            render: (content) => (
                <EmailShareButton url={url} subject={title} body={`${title}\n`}>{content}</EmailShareButton>
            ),
            icon: <EmailIcon size={ICON_SIZE} round />,
        },
    ];

    return (
        <Modal show={show} onHide={onHide} centered aria-labelledby="share-video-modal-title">
            <Modal.Header closeButton>
                <Modal.Title id="share-video-modal-title" as="h5">Chia sẻ</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="share-modal-targets">
                    {targets.map((target) => (
                        <div key={target.label} className="share-modal-target">
                            {target.render(
                                <span className="share-modal-target-inner">
                                    {target.icon}
                                    <span>{target.label}</span>
                                </span>
                            )}
                        </div>
                    ))}
                </div>

                <div className="share-modal-url mt-3">
                    <input
                        ref={inputRef}
                        type="text"
                        readOnly
                        value={url}
                        aria-label="Đường dẫn video"
                        onFocus={(event) => event.target.select()}
                    />
                    {/* Khong dung class .btn: home.css nap truoc Bootstrap nen .btn se de len style nut. */}
                    <button
                        type="button"
                        className="share-modal-copy"
                        onClick={handleCopy}
                    >
                        {copyStatus === 'copied' ? (
                            <><i className="fas fa-check me-1" />Đã sao chép</>
                        ) : 'Sao chép'}
                    </button>
                </div>
                {copyStatus === 'failed' && (
                    <small className="d-block text-danger mt-2" role="alert">
                        Trình duyệt không cho sao chép tự động. Đường dẫn đã được chọn sẵn, hãy nhấn Ctrl+C (hoặc giữ để sao chép trên điện thoại).
                    </small>
                )}
            </Modal.Body>
        </Modal>
    );
}
